import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../Common/LoadingSpinner';

const OTPVerification = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [timer, setTimer] = useState(300); // 5 minutes
  const [canResend, setCanResend] = useState(false);

  const { verifyOTP, resendOTP } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const userId = location.state?.userId;
  const email = location.state?.email;

  const inputRefs = useRef([]);

  useEffect(() => {
    if (!userId) {
      toast.error('Invalid verification link. Please login again.');
      navigate('/login');
      return;
    }

    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [userId, navigate]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');

    if (pastedData.length === 6) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpString = otp.join('');

    if (otpString.length !== 6) {
      toast.error('Please enter complete 6-digit OTP');
      return;
    }

    setLoading(true);

    try {
      // The instruction implies verifyOTP might return a response object with a success property.
      // Assuming verifyOTP either throws an error on failure or returns an object with success: false.
      // If verifyOTP only throws on error, the 'if (response.success)' check might be redundant
      // or indicate a change in the verifyOTP function's contract.
      // For now, we'll adapt to the instruction's structure.
      const response = await verifyOTP(userId, otpString); // Using otpString as per original logic
      if (response && response.success) { // Added check for response existence
        toast.success('Email verified successfully!');
        navigate('/dashboard', { replace: true });
      } else {
        // If verifyOTP returns a non-successful response object but doesn't throw
        throw new Error(response?.message || 'Verification failed. Please check the code.');
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      toast.error(error?.message || 'Verification failed. Please check the code.');
      // Auto-clear only if it was an invalid code error
      if (error?.message?.toLowerCase().includes('invalid')) {
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend || resendLoading) return;

    setResendLoading(true);
    try {
      await resendOTP(userId);
      toast.success('New OTP sent to your email');

      setTimer(300);
      setCanResend(false);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();

      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error('Resend OTP error:', error);
    } finally {
      setResendLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="auth-page">
      <div className="floating-shapes">
        <div className="floating-shape"></div>
        <div className="floating-shape"></div>
        <div className="floating-shape"></div>
        <div className="floating-shape"></div>
        <div className="floating-shape"></div>
        <div className="floating-shape"></div>
      </div>

      <div className="wave-container">
        <div className="wave"></div>
        <div className="wave"></div>
        <div className="wave"></div>
      </div>

      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="logo">
              <img src="/logo.svg" alt="MessagePe" className="auth-logo-img" />
            </div>
            <h2>Verify Your Email</h2>
            <p>
              We've sent a 6-digit verification code to<br />
              <strong>{email || 'your email'}</strong>
            </p>
          </div>

          <div className="otp-container">
            <form onSubmit={handleSubmit}>
              <div className="otp-input-group" onPaste={handlePaste}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="otp-input"
                    disabled={loading}
                    autoComplete="off"
                  />
                ))}
              </div>

              <div className="otp-timer">
                {timer > 0 ? (
                  <span>Code expires in {formatTime(timer)}</span>
                ) : (
                  <span className="text-danger">Code expired</span>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || otp.join('').length !== 6}
              >
                {loading ? <LoadingSpinner size="small" /> : 'Verify Email'}
              </button>
            </form>

            <div className="resend-section">
              <p>Didn't receive the code?</p>
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={!canResend || resendLoading}
                className="resend-otp"
              >
                {resendLoading ? 'Sending...' : 'Resend Code'}
              </button>
            </div>
          </div>

          <div className="auth-footer">
            <p>
              <Link to="/login">← Back to Login</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;